import { CategoryDto } from '../../commonTypes/documents.types';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { Button, MixerLoader, PremiumTooltip } from '../../components/Common';
import { useCanCreate } from '../../hooks/features';
import { Card } from './Card';
import { useHttpDeleteCategory, useHttpGetCategories } from '../../hooks';
import { Modal, YesNo } from '../../components/Common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFeaturePlanLimits } from '../../hooks/features';
import { Feature } from '@cyberlabrs/helpers';

interface ICategory {
    categories: { category: CategoryDto; documentCount: number }[];
}

const DocumentCategoriesSafetyWrapper = () => {
    const { areCategoriesReady, categories } = useHttpGetCategories();
    if (!areCategoriesReady) return <MixerLoader position="centerOnParent" />;
    return <DocumentCategories categories={categories} />;
};

const DocumentCategories = ({ categories }: ICategory) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const categoryToDeleteId = useRef<string>();
    const { exec } = useHttpDeleteCategory();
    const [categoriesWithCountState, setCategoriesWithCountState] = useState<{ category: CategoryDto; documentCount: number }[]>([]);
    const canCreate = useCanCreate(Feature.DOCUMENTS, 'createCategory', categoriesWithCountState.length);
    const categoryDtos = categoriesWithCountState.map(c => c.category);

    const {
        disabledItemIds: disabledCategoryIds,
        isLimitReached,
        shouldShowPremiumTooltip,
    } = useFeaturePlanLimits(Feature.DOCUMENTS, 'createCategory', categoryDtos, undefined, category => category.name === 'General Documents');

    useEffect(() => {
        setCategoriesWithCountState(categories);
    }, [categories]);

    const sortedCategories = useMemo(() => {
        const enabled: typeof categoriesWithCountState = [];
        const disabled: typeof categoriesWithCountState = [];

        categoriesWithCountState.forEach(cat => {
            if (disabledCategoryIds.has(cat.category.id)) {
                disabled.push(cat);
            } else {
                enabled.push(cat);
            }
        });

        const sortByCreatedAt = (a: typeof categoriesWithCountState[0], b: typeof categoriesWithCountState[0]) => {
            const aDate = new Date(a.category.createdAt).getTime();
            const bDate = new Date(b.category.createdAt).getTime();
            return aDate - bDate;
        };

        enabled.sort(sortByCreatedAt);
        disabled.sort(sortByCreatedAt);

        return [...enabled, ...disabled];
    }, [categoriesWithCountState, disabledCategoryIds]);

    const openDeleteModal = (id: string) => {
        categoryToDeleteId.current = id;
        setIsDeleteModalOpen(true);
    };

    const deleteCategory = (id?: string) => {
        if (id) exec(id);
        setCategoriesWithCountState(prev => prev.filter(cat => cat.category.id !== id));
        setIsDeleteModalOpen(false);
    };

    const { isXSmall, isSmall, isMedium } = useResizeListener();

    const isMobile = isXSmall || isSmall || isMedium;

    const handleCreateCategory = () => {
        if (!canCreate.canCreate) return;
        navigate('/create-category');
    };

    return (
        <div className="documents">
            {!isMobile && (
                <div className="documents__header">
                    <h2>{t('documents.documentsManagment')}</h2>
                    <div className={isLimitReached && shouldShowPremiumTooltip ? 'premium-tooltip-wrapper' : ''}>
                        <Button
                            text={t('createCategory.pageTitle.addCategory')}
                            onClick={handleCreateCategory}
                            disabled={!canCreate.canCreate || isLimitReached}
                        />
                        {(!canCreate.canCreate || (isLimitReached && shouldShowPremiumTooltip)) && <PremiumTooltip showOnHover position="bottom" />}
                    </div>
                </div>
            )}
            <div className="documents__main">
                <h4>{t('documents.yourCategories')}</h4>
                <div className="documents__list">
                    {sortedCategories.map(cat => (
                        <Card
                            key={cat.category.id}
                            category={cat.category}
                            onDelete={openDeleteModal}
                            documentCount={cat.documentCount}
                            isDisabled={disabledCategoryIds.has(cat.category.id)}
                            showPremiumTooltip={shouldShowPremiumTooltip}
                        />
                    ))}
                    {isMobile && (
                        <div className={isLimitReached && shouldShowPremiumTooltip ? 'premium-tooltip-wrapper' : ''}>
                            <Button
                                text={t('createCategory.pageTitle.addCategory')}
                                onClick={handleCreateCategory}
                                disabled={!canCreate.canCreate || isLimitReached}
                            />
                            {(!canCreate.canCreate || (isLimitReached && shouldShowPremiumTooltip)) && (
                                <PremiumTooltip showOnHover position="bottom" />
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Modal isVisible={isDeleteModalOpen} closeModal={() => setIsDeleteModalOpen(false)}>
                <YesNo
                    onNo={() => setIsDeleteModalOpen(false)}
                    onYes={() => deleteCategory(categoryToDeleteId.current)}
                    title={t('deleteCategory.deleteTitle')}
                    description={t('deleteCategory.deleteDescription')}
                />
            </Modal>
        </div>
    );
};

export default DocumentCategoriesSafetyWrapper;
